import { useState, useEffect, useCallback } from 'react';
import { parse } from 'querystring';
import { openRenewModal } from '@food/auth/build/dist';
import { UserAchievementsDataType } from './reservedContent';

export const html = (html: any) => ({ __html: html });

/**
 * Dato un utente, controlla se ha l'achievement specificato come secondo parametro
 *
 * Nota bene: viene volutamente tenuta divisa dalla funzione analoga relativa all'applicazione per accesso a contenuti
 * riservati (al momento `typeformVisibilityCheck` in `src/utils/reservedContent.tsx`) per motivi di dati: mentre
 * qui l'utente e' gia' stato sicuramente scaricato dal componente UserExtendedData, quindi qui posso permettermi di
 * avere gia' tutti i dati in input e quindi di definire una funzione sincrona.
 *
 * In realta' si potrebbe usare anche quella asincrona, ma questo comporterebbe la riscrittura di alcuni componenti; per
 * ora non e' una priorita'.
 *
 * @param user: User
 * @param achievement: string
 * @return boolean
 */
export const hasAchievement = (
	user: UserAchievementsDataType | null | undefined,
	achievement: string,
): boolean => {
	return (
		hasLocalAchievement(achievement) ||
		(!!user &&
			user.userAchievements &&
			user.userAchievements.some((a) => a && a.achievement === achievement))
	);
};

/**
 * Funzione per controllare la presenza di un achievement in localstorage
 * @param achievement: string
 * @returns boolean
 */
export const hasLocalAchievement = (achievement: string): boolean =>
	!!localStorage.getItem(achievement);

export const setLocalAchievement = (achievement: string): void =>
	localStorage.setItem(achievement, 'true');

export type LocalStorageKeysFilter = (s: string) => boolean;

const getAllLocalStorageKey = (f: LocalStorageKeysFilter) =>
	Object.entries(localStorage)
		.filter(([key]) => f(key))
		.reduce((tot, [key, value]) => Object.assign(tot, { [key]: value }), {});

export const useLocalStorage = (filterFunction: LocalStorageKeysFilter) => {
	const [valuableKeys, setValuableKeys] = useState(
		getAllLocalStorageKey(filterFunction),
	);

	const listener = useCallback(
		(ev) => {
			if (ev.key !== null && filterFunction(ev.key)) {
				setValuableKeys((keys) => ({
					...keys,
					[ev.key as string]: ev.newValue,
				}));
			}
		},
		[filterFunction],
	);

	useEffect(() => {
		window.addEventListener('storage', listener);
		return () => window.removeEventListener('storage', listener);
	}, [listener]);

	const muter = useCallback(
		(key: string, value: string) => {
			localStorage.setItem(key, value);
			setValuableKeys({
				...valuableKeys,
				[key]: value,
			});
		},
		[setValuableKeys, valuableKeys],
	);

	return [valuableKeys, muter];
};

const getSearchFromUrl = (url: string | null): string | null => {
	if (url === null) {
		return null;
	}
	const splitted = url.split('?');
	if (splitted.length < 2) {
		return null;
	}
	return splitted[1];
};

/**
 * Funzione di hash utile per trasformare stringe "pericolose" (tipo gli url dei form Pardot da usare come achievement)
 * in qualcosa di piu' omogeneo e meno esplicito
 * @param s: string
 * @return string
 */
export const hash = (s: string): string => {
	let hash = 0,
		i,
		c;
	const strlen = s.length;
	if (strlen === 0) {
		return hash.toString();
	}
	for (i = 0; i < strlen; i++) {
		c = s.charCodeAt(i);
		hash = (hash << 5) - hash + c;
		hash = hash & hash; // Convert to 32bit integer
	}
	return hash.toString();
};

/**
 * Utility per controllare la presenza di achievement nell'url e, in caso, impostare i dati in localstorage
 * @return void
 */
export const checkForAchievements = (): void => {
	// uso sia i parametri nella location che nell'hash: questo perche' potrei essere in un HistoryRouter di React
	// (e quindi usare window.location) oppure in un HashRouter, e quindi avrei una location simulata nell'hash
	// in questo modo riesco a gestire contemporaneamente entrambe i casi
	const locationArgs = parse(window.location.search.substring(1));
	const hashArgs = parse(getSearchFromUrl(window.location.hash) || '');
	const args = { ...locationArgs, ...hashArgs };

	if (args.achievement && typeof args.achievement === 'string') {
		setLocalAchievement(decodeURIComponent(args.achievement));
		// ri-eseguo il check dei permessi alla luce del nuovo achievement presente in localstorage andando nella pagina
		// che esegue i controlli
		const hash = ((window as any).location.hash) ? (window as any).location.hash.split(/[?\?]/)[0] : '';
		window.history.replaceState(null, '', window.location.pathname+hash);
	}
};

/**
 * Controlla la location per una richiesta di apertura modale, a volte forzata per richiesta di varie newsletter
 * @return void
 */
export const checkForAskAuth = (): void => {
	// uso sia i parametri nella location che nell'hash: questo perche' potrei essere in un HistoryRouter di React
	// (e quindi usare window.location) oppure in un HashRouter, e quindi avrei una location simulata nell'hash
	// in questo modo riesco a gestire contemporaneamente entrambe i casi
	const locationArgs = parse(window.location.search.substring(1));
	const hashArgs = parse(getSearchFromUrl(window.location.hash) || '');
	const args = { ...locationArgs, ...hashArgs };

	if (args.askAuth && args.askAuth === 'true') {
		openRenewModal(args.traditional === 'true' ? { startTraditional: true } : {});
	}
};

export function identity<T>(i: T) {
	return i;
}

// crea una stringa url-friendly
export const queryStringValue = (string: string): string => {
	string = string.toLowerCase().replace(/[\s\t\n\r]/gi, '-');
	return string;
};
// crea una stringa url-friendly; se è presente un array come secondo elemento, verifica che sia un valore univoco
export const uniqueQueryStringValue = (string: string, array?: string[]): string => {
	const clean = queryStringValue(string);
	string = clean;

	if (array && array.indexOf(string) !== -1) {
		let i = 1;
		do {
			string = clean + '_' + i;
			i++;
		} while (array.indexOf(string) !== -1);
	}

	return string;
};
